import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import jwtDecode from 'jwt-decode';

export interface UserProfile {
  id: string;
  name: string;
  displayName?: string;
  email: string;
  phone?: string;
  role: string;
  avatarUrl?: string;
  status?: string;
}

// GET /auth/me returns these fields at the top level (no `data` wrapper)
export interface MeResponse {
  name: string;
  email: string;
  role: string;
  issued_at: string;
  expires_at: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  id: string | null;
  displayName: string | null;
  email: string | null;
  phone: string | null;
  role: string | null;
  status: string | null;
  avatarUrl: string | null;
  // ISO date-time from /auth/me; the owner is logged out when it passes
  sessionExpiresAt: string | null;
  isInitialized: boolean;
  loading: boolean;
  error: string | null;
}

const loggedOutState: AuthState = {
  isAuthenticated: false,
  accessToken: null,
  refreshToken: null,
  id: null,
  displayName: null,
  email: null,
  phone: null,
  role: null,
  status: null,
  avatarUrl: null,
  sessionExpiresAt: null,
  isInitialized: true,
  loading: false,
  error: null,
};

const getInitialAuthState = (): AuthState => {
  let token: string | null = null;
  if (typeof window !== 'undefined') {
    token = localStorage.getItem('accessToken');
  }

  if (token) {
    try {
      const decoded: any = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (!decoded.exp || decoded.exp > currentTime) {
        const subRole = decoded?.sub || 'owner';
        return {
          isAuthenticated: true,
          accessToken: token,
          refreshToken: null,
          id: subRole,
          displayName: subRole.toUpperCase() === 'OWNER' ? 'ElectionSetu Owner' : 'Admin User',
          email: 'electionsetu@gmail.com',
          phone: null,
          role: subRole.toUpperCase(),
          status: 'active',
          avatarUrl: null,
          sessionExpiresAt: decoded.exp ? new Date(decoded.exp * 1000).toISOString() : null,
          isInitialized: true,
          loading: false,
          error: null,
        };
      }
    } catch (e) {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('accessToken');
      }
    }
  }

  return loggedOutState;
};

const initialState: AuthState = getInitialAuthState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setAuthError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    loginSuccessReducer: (
      state,
      action: PayloadAction<{ user: UserProfile; accessToken: string; refreshToken?: string }>
    ) => {
      const { user, accessToken, refreshToken } = action.payload;
      state.isAuthenticated = true;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken || null;
      state.id = user.id;
      state.displayName = user.name || user.displayName || user.email;
      state.email = user.email;
      state.phone = user.phone || null;
      state.role = user.role || 'SUPER_ADMIN';
      state.status = user.status || 'active';
      state.avatarUrl = user.avatarUrl || null;
      try {
        const { exp } = jwtDecode<{ exp?: number }>(accessToken);
        state.sessionExpiresAt = exp ? new Date(exp * 1000).toISOString() : null;
      } catch {
        state.sessionExpiresAt = null;
      }
      state.loading = false;
      state.error = null;
    },
    setMeReducer: (state, action: PayloadAction<MeResponse>) => {
      const { name, email, role, expires_at } = action.payload;
      if (name) state.displayName = name;
      if (email) state.email = email;
      // Stored uppercase, same as the login flow
      if (role) state.role = role.toUpperCase();
      if (expires_at) state.sessionExpiresAt = expires_at;
    },
    // Not the load-time initialState: that one is logged in when a token was stored
    logoutReducer: () => loggedOutState,
    setInitializedReducer: (state) => {
      state.isInitialized = true;
    },
  },
});

export const {
  setAuthLoading,
  setAuthError,
  loginSuccessReducer,
  setMeReducer,
  logoutReducer,
  setInitializedReducer,
} = authSlice.actions;

export default authSlice.reducer;
